import { Stack, Text, Title } from "@mantine/core";
import dayjs from "dayjs";
import { AppWebviewButton } from "./AppWebviewButton";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";
import classes from "./appWebviewShared.module.css";

interface ForecastBannerProps {
  teaserText: string;
  calculatedAt?: string;
  onContact?: () => void;
}

/**
 * The backend decides what to emphasise, so the copy arrives with two markers: *...* around
 * the words to enlarge, and a newline where the line should break. Nothing else is
 * interpreted, so no markup can reach the DOM through the copy.
 */
const EMPHASIS_PATTERN = /\*([^*]+)\*/;

/** Splitting on a capturing group alternates plain copy (even) and emphasised parts (odd). */
function renderTeaser(text: string) {
  // One block per line, so the lines sit on the surrounding Stack's gap. The copy arrives
  // padded with stray whitespace around the markers, which would render as blank lines.
  return text
    .trim()
    .split("\n")
    .map((line, lineIndex) => (
      <Text key={lineIndex} size="md">
        {line
          .trim()
          .split(EMPHASIS_PATTERN)
          .map((part, index) =>
            index % 2 === 1 ? (
              <Text
                key={index}
                span
                fw={700}
                fz={24}
                className={REPLAY_BLOCK_CLASS}
              >
                {part}
              </Text>
            ) : (
              <Text key={index} size="sm">
                {part}
              </Text>
            ),
          )}
      </Text>
    ));
}

export function ForecastBanner({
  teaserText,
  calculatedAt,
  onContact,
}: ForecastBannerProps) {
  return (
    <Stack gap={4} className={classes.forecastCard}>
      {onContact ? (
        <>
          <Text fw={700} tt="uppercase" size="sm">
            Möjlig avgiftssänkning
          </Text>
          {renderTeaser(teaserText)}
          <AppWebviewButton
            card
            justify="center"
            onClick={onContact}
            mt={8}
            h={42}
            fz="md"
          >
            Få hjälp med din pension
          </AppWebviewButton>
          {calculatedAt && (
            <Text
              size="xs"
              ta="center"
              mt={8}
              c="var(--wallet-color-neutrals-gray-700)"
              ff="var(--wallet-font-family-sambla-sans)"
            >
              Senast uppdaterad: {dayjs(calculatedAt).format("YYYY/MM/DD")}
            </Text>
          )}
        </>
      ) : (
        <>
          <Title
            order={2}
            fz="h3"
            lh="h3"
            ff="var(--wallet-font-family-sambla-sans)"
          >
            Vi kommer att höra av oss!
          </Title>
          <Text>
            En av våra pensionsrådgivare kontaktar dig för att gå igenom din
            pension och se hur den kan förbättras.
          </Text>
        </>
      )}
    </Stack>
  );
}
